import { useEffect, useState } from "react";
import api, { API } from "@/api";
import { useI18n } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { FileText, TrendingUp, DollarSign, AlertCircle } from "lucide-react";

const statusStyle = {
  paid: "bg-emerald-50 text-emerald-700",
  partial: "bg-amber-50 text-amber-700",
  unpaid: "bg-rose-50 text-rose-700",
};

export default function Reports() {
  const { t } = useI18n();
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [data, setData] = useState(null);

  const load = async () => {
    const params = new URLSearchParams();
    if (start) params.set("start_date", start);
    if (end) params.set("end_date", end);
    const { data } = await api.get("/reports/sales?" + params.toString());
    setData(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const downloadPdf = () => {
    const token = localStorage.getItem("access_token") || "";
    const params = new URLSearchParams({ token, lang: "ar" });
    if (start) params.set("start_date", start);
    if (end) params.set("end_date", end);
    window.open(`${API}/reports/sales/pdf?${params.toString()}`, "_blank");
  };

  const stats = data ? [
    { key: "totalBookings", value: data.totals.bookings, icon: FileText, color: "text-purple-700 bg-purple-50" },
    { key: "totalRevenue", value: `$${data.totals.revenue.toLocaleString()}`, icon: TrendingUp, color: "text-emerald-700 bg-emerald-50" },
    { key: "collected", value: `$${data.totals.collected.toLocaleString()}`, icon: DollarSign, color: "text-blue-700 bg-blue-50" },
    { key: "outstanding", value: `$${data.totals.outstanding.toLocaleString()}`, icon: AlertCircle, color: "text-rose-700 bg-rose-50" },
  ] : [];

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="uppercase-label">{t("salesReport")}</div>
          <h1 className="display-font text-3xl font-bold text-[hsl(var(--primary))] mt-1">{t("salesReport")}</h1>
          <div className="mt-3 divider-accent" />
        </div>
        <Button onClick={downloadPdf} className="btn-accent" data-testid="report-download-pdf">
          <FileText size={16} className="me-2" /> {t("downloadPdf")}
        </Button>
      </div>

      <div className="dashboard-card">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
          <Field label={t("startDate")}>
            <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="report-start-date" />
          </Field>
          <Field label={t("endDate")}>
            <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="report-end-date" />
          </Field>
          <Button onClick={load} className="bg-[hsl(var(--primary))] hover:opacity-90 text-white" data-testid="report-apply-btn">
            {t("apply")}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((s) => (
          <div key={s.key} className="dashboard-card fade-up" data-testid={`report-stat-${s.key}`}>
            <div className={"w-10 h-10 rounded-lg grid place-items-center mb-3 " + s.color}>
              <s.icon size={18} />
            </div>
            <div className="uppercase-label">{t(s.key)}</div>
            <div className="stat-value mt-1">{s.value}</div>
          </div>
        ))}
      </div>

      <div className="dashboard-card">
        <div className="uppercase-label mb-3">{t("bookingsDetail")}</div>
        {(!data || data.bookings.length === 0) ? (
          <div className="text-center py-10 text-sm text-[hsl(var(--muted-foreground))]">{t("empty")}</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("refNo")}</TableHead>
                <TableHead>{t("client")}</TableHead>
                <TableHead>{t("program")}</TableHead>
                <TableHead>{t("totalAmount")}</TableHead>
                <TableHead>{t("deposit")}</TableHead>
                <TableHead>{t("balanceDue")}</TableHead>
                <TableHead>{t("paymentMethod")}</TableHead>
                <TableHead>{t("paymentStatus")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.bookings.map((b) => (
                <TableRow key={b.id}>
                  <TableCell className="font-mono text-xs">{b.ref_no}</TableCell>
                  <TableCell className="font-semibold">{b.client?.name || "—"}</TableCell>
                  <TableCell>{b.program?.name || "—"}</TableCell>
                  <TableCell>{(b.total_amount || 0).toLocaleString()} {b.currency}</TableCell>
                  <TableCell>{(b.deposit || 0).toLocaleString()} {b.currency}</TableCell>
                  <TableCell className={(b.balance || 0) > 0 ? "text-rose-600 font-semibold" : "text-emerald-600 font-semibold"}>
                    {(b.balance || 0).toLocaleString()} {b.currency}
                  </TableCell>
                  <TableCell className="text-sm">{b.payment_method || "—"}</TableCell>
                  <TableCell>
                    <span className={"inline-flex items-center px-2 py-0.5 rounded-full text-xs " + (statusStyle[b.payment_status] || statusStyle.unpaid)}>
                      {t(b.payment_status === "paid" ? "paidFull" : b.payment_status === "partial" ? "partial" : "unpaid")}
                    </span>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="text-xs uppercase-label">{label}</Label>
    {children}
  </div>
);
